import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import GlassCard from "@/components/common/GlassCard";
import CrudDrawer from "@/components/common/CrudDrawer";
import StatusBadge from "@/components/common/StatusBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { CRUD } from "@/constants/testIds";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const PAGE_SIZE = 25;

function labelFor(item, config) {
  return item?.[config.titleKey || "title"] || item?.name || "This item";
}

export default function GenericCRUD({ config }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [selected, setSelected] = useState([]);
  const [sortKey, setSortKey] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  const [createSeed, setCreateSeed] = useState(null);
  const editParam = searchParams.get("edit") || "";

  const hasOrderField = (config.fields || []).some((f) => f.key === "order");
  const hasStatus = Boolean(config.statusFilter?.length);

  const nudgeOrder = async (item, dir) => {
    const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
    const idx = sorted.findIndex((i) => i.id === item.id);
    const swapWith = sorted[idx + dir];
    if (!swapWith) return;
    const aOrder = item.order ?? idx + 1;
    const bOrder = swapWith.order ?? idx + dir + 1;
    try {
      await Promise.all([
        api.patch(`/${config.resource}/${item.id}`, { order: bOrder }),
        api.patch(`/${config.resource}/${swapWith.id}`, { order: aOrder }),
      ]);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Reorder failed");
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get(`/${config.resource}`, { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelected([]);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [q, statusFilter, page]); // eslint-disable-line

  useEffect(() => { setPage(1); }, [q, statusFilter]);

  // Deep-link from SEO: /blog?edit=slug or /services?edit=slug
  useEffect(() => {
    if (!editParam || loading) return;
    let cancelled = false;
    const mergeItem = (item) => {
      const defaults = config.defaultValues || {};
      return {
        ...defaults,
        ...item,
        seo: { ...(defaults.seo || {}), ...((item && item.seo) || {}) },
      };
    };
    const openFromParam = async () => {
      const match = (items || []).find(
        (it) => it.id === editParam || it.slug === editParam || String(it._id || "") === editParam,
      );
      if (match) {
        if (!cancelled) {
          setEditing(mergeItem(match));
          setOpen(true);
        }
        return;
      }
      try {
        const { data } = await api.get(`/${config.resource}`, { params: { q: editParam, page_size: 50 } });
        const found = (data.items || []).find(
          (it) => it.id === editParam || it.slug === editParam || String(it._id || "") === editParam,
        ) || (data.items || [])[0];
        if (found && !cancelled) {
          setEditing(mergeItem(found));
          setOpen(true);
        }
      } catch {
        /* ignore — user can pick manually */
      }
    };
    openFromParam();
    return () => { cancelled = true; };
  }, [editParam, loading, items, config.resource]);

  const clearEditParam = () => {
    if (!editParam) return;
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
  };

  const displayed = useMemo(() => {
    if (!sortKey) return items;
    const next = [...items];
    next.sort((a, b) => {
      const av = (a?.[sortKey] ?? "").toString().toLowerCase();
      const bv = (b?.[sortKey] ?? "").toString().toLowerCase();
      if (av < bv) return sortDir === "asc" ? -1 : 1;
      if (av > bv) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
    return next;
  }, [items, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(key); setSortDir("asc"); }
  };

  const suggestedOrder = () => {
    const maxFromItems = items.reduce((m, it) => Math.max(m, Number(it.order) || 0), 0);
    return Math.max(Number(total) || 0, maxFromItems, items.length) + 1;
  };

  const openCreate = () => {
    const defaults = { ...(config.defaultValues || {}) };
    const wantsOrder =
      hasOrderField
      || Object.prototype.hasOwnProperty.call(defaults, "order")
      || (config.fields || []).some((f) => f.key === "order");
    if (wantsOrder) {
      defaults.order = suggestedOrder();
    }
    setCreateSeed(defaults);
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (item) => {
    const defaults = config.defaultValues || {};
    const merged = {
      ...defaults,
      ...item,
      seo: { ...(defaults.seo || {}), ...((item && item.seo) || {}) },
    };
    setCreateSeed(null);
    setEditing(merged);
    setOpen(true);
  };

  const save = async (values) => {
    try {
      if (editing) {
        await api.patch(`/${config.resource}/${editing.id}`, values);
        toast.success("Updated");
      } else {
        await api.post(`/${config.resource}`, { ...config.defaultValues, ...values });
        toast.success("Created");
      }
      setOpen(false);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    }
  };

  const remove = async () => {
    const ids = pendingDelete === "bulk"
      ? selected
      : pendingDelete?.id ? [pendingDelete.id] : [];
    if (!ids.length) return;
    try {
      await Promise.all(ids.map((id) => api.delete(`/${config.resource}/${id}`)));
      toast.success(ids.length > 1 ? `${ids.length} deleted` : "Deleted");
      setPendingDelete(null);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to delete");
    }
  };

  const duplicate = async (item) => {
    const { id, _id, created_at, updated_at, ...rest } = item;
    const titleField = config.titleKey || (rest.title != null ? "title" : rest.name != null ? "name" : null);
    const payload = {
      ...config.defaultValues,
      ...rest,
      ...(titleField ? { [titleField]: `${rest[titleField] || "Item"} (copy)` } : {}),
    };
    if (Object.prototype.hasOwnProperty.call(payload, "order") || hasOrderField) {
      payload.order = suggestedOrder();
    }
    // New copies must not reuse unique slugs (blog/services/portfolio).
    if (Object.prototype.hasOwnProperty.call(payload, "slug") || rest.slug) {
      const baseTitle = titleField ? payload[titleField] : rest.title || rest.name || "item";
      payload.slug = String(baseTitle || "item")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
    }
    try {
      await api.post(`/${config.resource}`, payload);
      toast.success("Duplicated");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not duplicate");
    }
  };

  const bulkStatus = async (status) => {
    if (!selected.length) return;
    try {
      await Promise.all(selected.map((id) => api.patch(`/${config.resource}/${id}`, { status })));
      toast.success(`Updated ${selected.length} item(s)`);
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Bulk update failed");
    }
  };

  const allOnPageSelected = displayed.length > 0 && displayed.every((item) => selected.includes(item.id));
  const toggleAll = () => {
    if (allOnPageSelected) setSelected([]);
    else setSelected(displayed.map((item) => item.id));
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div data-testid={CRUD.root(config.resource)}>
      <PageHeader
        title={config.title}
        subtitle={config.subtitle}
        actions={
          <Button
            data-testid={CRUD.addBtn(config.resource)}
            onClick={openCreate}
            className="btn-gradient text-white border-0"
          >
            <Icons.Plus className="w-4 h-4 mr-1.5" /> Add new
          </Button>
        }
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input
            data-testid={CRUD.searchInput(config.resource)}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${config.title.toLowerCase()}…`}
            className="pl-9 bg-black/20 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        {hasStatus && (
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={() => setStatusFilter("")}
              className={`h-9 px-3 rounded-lg text-xs border transition-colors duration-200 ${!statusFilter ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/60 hover:text-white"}`}
            >All</button>
            {config.statusFilter.map((s) => (
              <button key={s}
                onClick={() => setStatusFilter(s)}
                className={`h-9 px-3 rounded-lg text-xs border transition-colors duration-200 capitalize ${statusFilter === s ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/60 hover:text-white"}`}
              >{s.replace("_", " ")}</button>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 glass rounded-xl px-3 py-2">
          <span className="text-xs text-white/60">{selected.length} selected</span>
          {hasStatus && config.statusFilter.map((s) => (
            <Button key={s} size="sm" variant="ghost" onClick={() => bulkStatus(s)} className="h-8 text-xs capitalize text-white/80 hover:text-white hover:bg-white/5">
              Mark {s.replace("_", " ")}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setPendingDelete("bulk")} className="h-8 text-xs text-red-200 hover:text-red-100 hover:bg-red-500/10 ml-auto">
            <Icons.Trash2 className="w-3.5 h-3.5 mr-1" /> Delete selected
          </Button>
        </div>
      )}

      <GlassCard className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white/[0.03] text-left text-[11px] uppercase tracking-wider text-white/45">
                <th className="px-4 py-3 w-10">
                  <Checkbox checked={allOnPageSelected} onCheckedChange={toggleAll} aria-label="Select all on page" />
                </th>
                {config.columns.map((c) => (
                  <th key={c.key} className={`px-5 py-3 font-medium ${c.className || ""}`}>
                    <button type="button" onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-white">
                      {c.label}
                      {sortKey === c.key ? (sortDir === "asc" ? <Icons.ArrowUp className="w-3 h-3" /> : <Icons.ArrowDown className="w-3 h-3" />) : <Icons.ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  </th>
                ))}
                <th className="px-5 py-3 font-medium text-right w-36">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-t border-white/5">
                    <td className="px-4 py-4" />
                    {config.columns.map((c) => (
                      <td key={c.key} className="px-5 py-4"><Skeleton className="h-4 w-32 bg-white/5" /></td>
                    ))}
                    <td className="px-5 py-4"></td>
                  </tr>
                ))
              ) : displayed.length === 0 ? (
                <tr>
                  <td colSpan={config.columns.length + 2} className="px-5 py-16 text-center">
                    <div className="flex flex-col items-center gap-3 text-white/40 max-w-sm mx-auto">
                      <div className="w-14 h-14 rounded-2xl glass flex items-center justify-center">
                        <Icons.Inbox className="w-7 h-7 text-white/50" />
                      </div>
                      <p className="text-white/70 text-sm font-medium">No {config.title.toLowerCase()} yet</p>
                      <p className="text-xs text-white/40">Create your first {config.singular} to show it on the live website.</p>
                      <Button onClick={openCreate} className="btn-gradient text-white border-0 mt-1">
                        <Icons.Plus className="w-4 h-4 mr-1.5" /> Add {config.singular}
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                displayed.map((item) => (
                  <tr key={item.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors duration-200">
                    <td className="px-4 py-4">
                      <Checkbox
                        checked={selected.includes(item.id)}
                        onCheckedChange={() => setSelected((cur) => cur.includes(item.id) ? cur.filter((id) => id !== item.id) : [...cur, item.id])}
                        aria-label={`Select ${labelFor(item, config)}`}
                      />
                    </td>
                    {config.columns.map((c) => (
                      <td key={c.key} className={`px-5 py-4 text-white/80 ${c.className || ""}`}>
                        {c.badge ? <StatusBadge value={item[c.key]} /> : c.render ? c.render(item) : (item[c.key] ?? "—")}
                      </td>
                    ))}
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        {hasOrderField && (
                          <>
                            <button
                              type="button"
                              onClick={() => nudgeOrder(item, -1)}
                              className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white transition-colors duration-200"
                              aria-label="Move up"
                              title="Move up"
                            ><Icons.ChevronUp className="w-4 h-4" /></button>
                            <button
                              type="button"
                              onClick={() => nudgeOrder(item, 1)}
                              className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white transition-colors duration-200"
                              aria-label="Move down"
                              title="Move down"
                            ><Icons.ChevronDown className="w-4 h-4" /></button>
                          </>
                        )}
                        <button
                          data-testid={CRUD.editBtn(config.resource, item.id)}
                          onClick={() => openEdit(item)}
                          className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white transition-colors duration-200"
                          aria-label="Edit"
                          title="Edit"
                        ><Icons.Pencil className="w-4 h-4" /></button>
                        <button
                          onClick={() => duplicate(item)}
                          className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-white transition-colors duration-200"
                          aria-label="Duplicate"
                          title="Duplicate"
                        ><Icons.Copy className="w-4 h-4" /></button>
                        <button
                          data-testid={CRUD.deleteBtn(config.resource, item.id)}
                          onClick={() => setPendingDelete(item)}
                          className="p-2 rounded-md hover:bg-white/5 text-white/60 hover:text-red-300 transition-colors duration-200"
                          aria-label="Delete"
                          title="Delete"
                        ><Icons.Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </GlassCard>

      {!loading && total > 0 && (
        <div className="mt-4 flex items-center justify-between text-xs text-white/50">
          <span>Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" disabled={page === 1} onClick={() => setPage((value) => value - 1)} className="text-white/70 hover:text-white">Previous</Button>
            <span className="self-center text-white/40">Page {page} of {pageCount}</span>
            <Button size="sm" variant="ghost" disabled={page * PAGE_SIZE >= total} onClick={() => setPage((value) => value + 1)} className="text-white/70 hover:text-white">Next</Button>
          </div>
        </div>
      )}

      <CrudDrawer
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) {
            clearEditParam();
            setCreateSeed(null);
          }
        }}
        title={editing ? `Edit ${config.singular}` : `New ${config.singular}`}
        fields={config.fields}
        initial={editing || createSeed || config.defaultValues}
        onSave={save}
        resourceKey={config.resource}
      />
      <AlertDialog open={!!pendingDelete} onOpenChange={(open) => !open && setPendingDelete(null)}>
        <AlertDialogContent className="glass-strong border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete === "bulk" ? `Delete ${selected.length} ${config.title.toLowerCase()}?` : `Delete this ${config.singular}?`}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">
              {pendingDelete === "bulk"
                ? "This cannot be undone from the admin panel."
                : `“${labelFor(pendingDelete || {}, config)}” will be removed. This action cannot be undone from the admin panel.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
